import { Button, Empty, Form, Spin } from "antd";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { LiveTrackingMap } from "../../components/live_tracking_map.component";
import { LiveVehiclePanel } from "../../components/live_vehicle_panel.component";
import { OfflinePanel } from "../../components/offline_panel.component";
import { SpeedingPanel } from "../../components/speeding_panel.component";
import { TrackingActivityHighlights } from "../../components/tracking_activity_panel.component";
import { MYCInput } from "../../components/input_form.component";
import { httpClient } from "../../api/httpClient";
import { Endpoints } from "../../constants/endpoint.constant";
import { useGet } from "../../hooks/useRest.hook";
import { useInfringementStream } from "../../hooks/useInfringementStream";
import { useTripTrackingActivity } from "../../hooks/useTripTrackingActivity";
import { useTripLiveTracking } from "../../hooks/useTripLiveTracking";
import { mergeTrackingWithLiveSamples } from "../../services/firestoreTracking.service";
import { UserContext } from "../../App";
import { TripStatus, getRegionalOriginIds, isRegionalManagerRole } from "../../constants/general.constant";
import { normalizeInspectionRecord } from "../../utils/inspection.util";

const SummaryCard = ({ label, value }) => <div className="rounded-[24px] border border-[#ece5dd] bg-white px-5 py-4 shadow-[0_12px_32px_rgba(15,23,42,0.05)]">
  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#a38b78]">{label}</div>
  <div className="mt-2 text-[1.15rem] font-semibold text-[#1c1713]">{value || "Not available"}</div>
</div>;

export const TrackOrderScreen = () => {
  const { user } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searching, setSearching] = useState(false);
  const [clockMs, setClockMs] = useState(Date.now());
  const selectedTripId = searchParams.get("tripId") || "";
  const regionalOriginIds = useMemo(() => getRegionalOriginIds(user), [user]);
  const isRegionalManager = isRegionalManagerRole(user);
  const tripRq = useGet(
    selectedTripId ? Endpoints.GET_TRIP_ORDER(selectedTripId) : "",
    Boolean(selectedTripId)
  );
  const preInspectionRq = useGet(
    selectedTripId ? Endpoints.GET_PRE_INS(selectedTripId) : "",
    Boolean(selectedTripId)
  );
  const tracking = useTripLiveTracking(selectedTripId, Boolean(selectedTripId));
  const trackingActivity = useTripTrackingActivity(selectedTripId, Boolean(selectedTripId));
  const tripInfringements = useInfringementStream({
    tripId: selectedTripId,
    filters: isRegionalManager ? { originIds: regionalOriginIds } : {},
    limitCount: 100,
    enabled: Boolean(selectedTripId),
    includeTripSubcollections: true,
  });

  const onFinish = async (values) => {
    const filter = (values?.search || "").trim();
    if (!filter) return;

    setSearching(true);
    try {
      const response = await httpClient.get(Endpoints.FIND_ORDER_REG_REF(filter));
      const nextTrip = response?.data?.data;
      if (!nextTrip?.id) {
        throw new Error("Trip order not found");
      }
      setSearchParams({ tripId: String(nextTrip.id) });
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to find trip order");
    } finally {
      setSearching(false);
    }
  };

  const trip = tripRq?.data?.id ? tripRq.data : null;
  const isTerminalTrip =
    Number(trip?.status) === TripStatus.Completed ||
    Number(trip?.status) === TripStatus.Cancelled;

  const preInspection = useMemo(
    () => normalizeInspectionRecord(preInspectionRq?.data, trip),
    [preInspectionRq?.data, trip]
  );

  useEffect(() => {
    if (!selectedTripId || isTerminalTrip) return undefined;
    const intervalId = setInterval(() => setClockMs(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [selectedTripId, isTerminalTrip]);

  useEffect(() => {
    if (!selectedTripId) return undefined;

    let cancelled = false;
    httpClient.get(Endpoints.GET_TRIP_COMPLIANCE(selectedTripId)).catch((error) => {
      if (cancelled) return;
      // The Firestore listener still renders available tracking data; this refresh only
      // nudges the backend to rebuild road/speed-limit context for stale trips.
      console.warn("[TRACK] compliance refresh failed", error?.message || error);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedTripId]);

  const trackingDataForTrip = String(tracking.data?.id || "") === String(selectedTripId)
    ? tracking.data
    : null;
  const liveTracking = useMemo(
    () => mergeTrackingWithLiveSamples(trackingDataForTrip, trackingActivity.trackingSamples, clockMs),
    [trackingDataForTrip, trackingActivity.trackingSamples, clockMs]
  );
  const trackingPointCount = trackingActivity.trackingSamples.length || trackingActivity.trackingEvents.length;
  const speedingRecords = tripInfringements.records.filter((record) => record?.type === "speeding");
  const latestSpeedingRecord = speedingRecords[0] || null;
  const offlineRecords = trackingActivity.offlineInfringements.length
    ? trackingActivity.offlineInfringements
    : tripInfringements.records.filter((record) => record?.type === "offline");
  const providerName =
    trackingDataForTrip?.summary?.providerName ||
    trackingDataForTrip?.summary?.speedingAnalysis?.providerName ||
    "tomtom-snap-to-roads";
  const providerStatus =
    trackingDataForTrip?.summary?.providerStatus ||
    trackingDataForTrip?.summary?.speedingAnalysis?.providerStatus;
  const providerMessage =
    trackingDataForTrip?.summary?.providerMessage ||
    trackingDataForTrip?.summary?.speedingAnalysis?.providerMessage ||
    null;

  return <div className="flex flex-col gap-8 w-full">
    <div className="rounded-[32px] border border-[#ece2d8] bg-[linear-gradient(135deg,#fffaf6_0%,#fff4ec_100%)] px-5 py-5 shadow-[0_24px_65px_rgba(90,62,40,0.08)] xl:px-6 xl:py-6">
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(320px,430px),1fr] xl:items-end">
          <div className="flex flex-col gap-2">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[#e15631] px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white">
              <SearchOutlined />
              Live Tracking
            </span>
            <h1 className="m-0 text-[28px] font-semibold leading-tight text-[#211c17]">Track vehicle telemetry in real time</h1>
            <p className="m-0 text-[14px] leading-6 text-[#6f6258]">
              Search by client reference, MYC reference, or vehicle registration.
            </p>
          </div>

          <Form onFinish={onFinish} layout="vertical" className="grid grid-cols-1 gap-3 rounded-[24px] border border-[#f1e5dc] bg-white px-5 py-4 shadow-[0_18px_40px_rgba(90,62,40,0.05)] lg:grid-cols-[1fr,auto] lg:items-end">
            <MYCInput
              name="search"
              label="Client Ref / MYC Ref / Vehicle Reg No"
              type="text"
              required
              className="mb-0"
            />
            <Button htmlType="submit" type="primary" loading={searching}>
              Track Vehicle
            </Button>
          </Form>
        </div>

        {selectedTripId ? <LiveTrackingMap
          trip={trip}
          tracking={liveTracking}
          infringements={tripInfringements.records}
          trackingEvents={trackingActivity.trackingEvents}
          trackingSamples={trackingActivity.trackingSamples}
          offlineAlertCount={offlineRecords.length}
          height={560}
        /> : null}
        {selectedTripId ? <TrackingActivityHighlights
          trackingEvents={trackingActivity.trackingEvents}
          complianceEvents={trackingActivity.complianceEvents}
        /> : null}
      </div>
    </div>

    {(tripRq?.loading || preInspectionRq?.loading || tracking.loading || trackingActivity.loading) && selectedTripId ? <div className="flex justify-center py-10"><Spin /></div> : null}

    {!selectedTripId ? <div className="rounded-[28px] border border-dashed border-[#e5ddd5] bg-white px-8 py-10">
      <Empty description="Search for a trip to begin live vehicle tracking." />
    </div> : null}

    {selectedTripId && !tripRq?.loading ? <>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <SummaryCard
          label="Last Mode"
          value={liveTracking?.trackingStatusLabel || "Live driver tracking"}
        />
        <SummaryCard
          label="Tracking Points"
          value={String(trackingPointCount || 0)}
        />
        <SummaryCard
          label="Trip"
          value={trip?.id ? `MYC #${trip.id}` : `tripOrders/${selectedTripId}`}
        />
      </div>

      <div>
        <LiveVehiclePanel
          trip={trip}
          preInspection={preInspection}
          tracking={liveTracking}
          trackingPointCount={trackingPointCount}
          speedingCount={speedingRecords.length}
          latestSpeedingRecord={latestSpeedingRecord}
          offlineCount={offlineRecords.length}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <SpeedingPanel
          records={speedingRecords}
          providerName={providerName}
          providerStatus={providerStatus || trackingDataForTrip?.summary?.speedingAnalysis?.providerStatus}
          providerMessage={providerMessage}
        />
        <OfflinePanel
          records={offlineRecords}
          providerName="driver-tracking-telemetry"
          providerStatus={providerStatus || trackingDataForTrip?.trackingStatusKey}
        />
      </div>
    </> : null}
  </div>;
};
